// MIDAS V3 — <LiveRowV3> per-row "verified live" pulse + its empty slot
// (MIDAS-ANSWER-FORMAT-1 round 5; Paper board S10, frame "Variant E" → the
// "live pulse" and "no live" cells of the E table).
//
//   <live-row/>  → green pulse waveform in the table's live-pulse cell
//   (no tag)     → same-width empty slot with a quiet dash, so the icons and
//                  the dashes form ONE vertical lane down the table
//
// Paper values:
//   slot   : 28 px wide, flex, centred, content pushed to the END (right)
//   pulse  : 22×14 svg, path "M1 7h4l2-5 3 10 3-7 2 2h6", stroke 1.6,
//            round caps + joins, #2E9E5B → --green (the TRUE/✓ green)
//   empty  : 10×2 bar, #E3DFD5 → --border
// Animation (not drawable on the board, specified in the bot request): a
// gentle ~1.5 s ease opacity/scale pulse; static under prefers-reduced-motion
// (decided in JS here AND by the media query in src-ref.css).
//
// The slot geometry is INLINE (not only in CSS) and shared by both variants,
// so "same width" is a property of the code, not of a stylesheet that could
// drift — and it is assertable in jsdom.
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import * as React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { Tooltip, TooltipTrigger } from "../v2/ui/tooltip";
import { prefersReducedMotion } from "../../utils/compRefFlash";
import { SrcInfoBoxV3 } from "./SrcRefV3";

/** Shared lane geometry — the marker and the empty slot MUST match. */
export const LIVE_ROW_SLOT_STYLE: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "flex-end",
  width: 28,
  minWidth: 28,
  height: "1.5em", // one table text line (td is 13 px / 1.5) — icon sits on it
  verticalAlign: "top",
  flexShrink: 0,
};

export const LiveRowV3: React.FC = () => {
  const { translate } = useTranslation("chatBot");
  const body = translate("liveRow.tooltip", {
    defaultValue: "Verified live on the machine this turn",
  }) as string;
  const eyebrow = translate("liveRow.eyebrow", { defaultValue: "Live" }) as string;
  const className = prefersReducedMotion()
    ? "live-row live-row--static"
    : "live-row";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={className}
          style={LIVE_ROW_SLOT_STYLE}
          role="img"
          aria-label={body}
          tabIndex={0}
          data-live-row="verified"
        >
          <svg
            className="live-row__pulse"
            width="22"
            height="14"
            viewBox="0 0 22 14"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M1 7h4l2-5 3 10 3-7 2 2h6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </TooltipTrigger>
      <SrcInfoBoxV3 eyebrow={eyebrow} body={body} />
    </Tooltip>
  );
};

/** Unverified row in a live lane: same slot, quiet dash, nothing to announce. */
export const LiveRowEmptySlotV3: React.FC = () => (
  <span
    className="live-row live-row--empty"
    style={LIVE_ROW_SLOT_STYLE}
    aria-hidden="true"
    data-live-row="empty"
  >
    <span className="live-row__dash" />
  </span>
);

export default LiveRowV3;
